import { ChevronDown } from 'lucide-react';
import { useId } from 'react';
import type { ChangeEventHandler, FocusEventHandler, Ref } from 'react';
import styles from './select-field.module.css';

export type SelectOption = {
  value: string;
  label: string;
};

/**
 * `value` is required on purpose. React switches an input from uncontrolled to controlled the
 * moment it first receives a string after an undefined, which costs the caret its position and
 * writes a warning to the console - and that is exactly what an edit form does when it resets
 * itself once the record has loaded. Wire this up through Controller, not register().
 */
type SelectFieldProps = {
  label: string;
  name: string;
  options: SelectOption[];
  value: string;
  onChange: ChangeEventHandler<HTMLSelectElement>;
  onBlur?: FocusEventHandler<HTMLSelectElement>;
  ref?: Ref<HTMLSelectElement>;
  placeholder?: string;
  hint?: string;
  error?: string;
  hideLabel?: boolean;
  disabled?: boolean;
};

export const SelectField = ({
  label,
  name,
  options,
  value,
  onChange,
  onBlur,
  ref,
  placeholder,
  hint,
  error,
  hideLabel = false,
  disabled = false,
}: SelectFieldProps) => {
  const selectId = useId();
  const messageId = `${selectId}-message`;
  const hasError = error !== undefined;
  const message = error ?? hint;

  return (
    <div className={styles.field}>
      <label className={hideLabel ? styles.labelHidden : styles.label} htmlFor={selectId}>
        {label}
      </label>
      <div className={styles.control}>
        <select
          id={selectId}
          ref={ref}
          className={`${styles.select} ${hasError ? styles.invalid : ''}`}
          name={name}
          value={value}
          disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={message === undefined ? undefined : messageId}
          onChange={onChange}
          onBlur={onBlur}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown className={styles.icon} />
      </div>
      {message !== undefined && (
        <p
          id={messageId}
          className={hasError ? styles.error : styles.hint}
          role={hasError ? 'alert' : undefined}
        >
          {message}
        </p>
      )}
    </div>
  );
};
